import { Panel, PageHeader } from "@/components/admin-shell";
import { apiRequest } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Download,
  Package,
  MapPin,
  CreditCard,
  Calendar,
  Hash,
  User,
  ShoppingBag,
  ExternalLink,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

interface IOrderDetail {
  _id: string;
  orderNumber: string;
  createdAt: string;
  status: "pending" | "confirmed" | "packed" | "shipped" | "delivered" | "cancelled";
  paymentMethod: string;
  bkashTxn?: string;
  subtotal?: number;
  discount?: number;
  shippingFee?: number;
  total: number;
  shipping: {
    name: string;
    phone: string;
    email?: string;
    address: string;
    area?: string;
    city?: string;
    district: string;
    postcode?: string;
  };
  items: {
    name: string;
    slug: string;
    image?: string;
    ml?: number;
    qty: number;
    price: number;
  }[];
  courier?: {
    trackingUrl: string;
    provider?: string;
  };
}

const STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];
const STAGES  = ["confirmed", "packed", "shipped", "out-for-delivery", "delivered"];

function stageIndex(status: string) {
  const idx = STAGES.indexOf(status);
  return idx === -1 ? 0 : idx;
}

function statusColor(status: string) {
  switch (status) {
    case "delivered":   return "text-emerald-300 border-emerald-500/30 bg-emerald-950/20";
    case "shipped":     return "text-blue-300 border-blue-500/30 bg-blue-950/20";
    case "pending":     return "text-gold border-gold/30 bg-gold/10";
    case "cancelled":   return "text-red-300 border-red-500/30 bg-red-950/20";
    default:            return "text-amber-300 border-amber-500/30 bg-amber-950/20";
  }
}

function formatWhatsApp(phone: string, orderNumber: string, name: string) {
  const clean = (phone || "").replace(/\D/g, "");
  const num = clean.startsWith("880")
    ? clean
    : clean.startsWith("0")
    ? "88" + clean
    : "880" + clean;
  const text = `Hello ${name || "there"}, we're reaching out regarding your Subaashghor order #${orderNumber}. How can we assist you?`;
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
}

const fmt = (n: number) => `৳${(n ?? 0).toLocaleString("en-BD")}`;

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: order, isLoading, error } = useQuery<IOrderDetail>({
    queryKey: ["order", id],
    queryFn: () => apiRequest<IOrderDetail>(`/orders/${id}`),
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ status }: { status: string }) =>
      apiRequest<IOrderDetail>(`/orders/${id}/status`, "PATCH", { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Order status updated");
    },
    onError: (err: any) => toast.error(err.message || "Failed to update status"),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-gold animate-pulse">
        Loading order details…
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-red-400 p-4">
        {error ? (error as Error).message : "Order not found."}
      </div>
    );
  }

  const customer    = order.shipping;
  const waUrl       = formatWhatsApp(customer.phone, order.orderNumber, customer.name);
  const idx         = stageIndex(order.status);
  const subtotal    = order.subtotal ?? order.total;
  const discount    = order.discount ?? 0;
  const shippingFee = order.shippingFee ?? 0;

  return (
    <>
      <PageHeader
        title={`Order #${order.orderNumber}`}
        subtitle={`Placed on ${new Date(order.createdAt).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })}`}
        action={
          <button
            onClick={() => navigate("/orders")}
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs font-semibold text-gold hover:bg-accent/50 transition"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Orders
          </button>
        }
      />

      {/* Status Progress Bar */}
      <Panel className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Hash className="h-4 w-4 text-gold-soft" />
            <span className="font-mono text-gold text-lg font-bold">{order.orderNumber}</span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Status Updater */}
            <select
              value={order.status}
              onChange={(e) => {
                const newStatus = e.target.value;
                if (window.confirm(`Change status to "${newStatus}"?`)) {
                  updateStatusMutation.mutate({ status: newStatus });
                }
              }}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold capitalize outline-none cursor-pointer transition ${statusColor(order.status)}`}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s} className="bg-brand-card text-foreground capitalize">
                  {s}
                </option>
              ))}
            </select>

            {order.courier && (
              <a
                href={order.courier.trackingUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 px-4 py-1.5 text-xs text-gold hover:bg-accent/50 transition"
              >
                Track Courier <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>

        {/* Progress Track */}
        <div className="space-y-2">
          <div className="flex gap-1.5">
            {STAGES.map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-2 rounded-full transition-all ${
                  i <= idx ? "bg-gold" : "bg-gold/15"
                }`}
              />
            ))}
          </div>
          <div className="grid grid-cols-5 text-[10px] uppercase tracking-widest text-center">
            {["Confirmed", "Packing", "Shipped", "Out for Delivery", "Delivered"].map((s, i) => (
              <span key={s} className={i <= idx ? "text-gold font-bold" : "text-muted-foreground"}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </Panel>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT — Customer & Communication */}
        <div className="lg:col-span-1 space-y-6">

          {/* Customer Info */}
          <Panel title="Customer Details">
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 text-gold-soft mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Name</p>
                  <p className="font-semibold text-foreground">{customer.name || "—"}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-gold-soft mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Phone</p>
                  <p className="font-semibold text-foreground">{customer.phone || "—"}</p>
                </div>
              </div>

              {customer.email && (
                <div className="flex items-start gap-3">
                  <MessageCircle className="h-4 w-4 text-gold-soft mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Email</p>
                    <p className="text-foreground">{customer.email}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-gold-soft mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Delivery Address</p>
                  <p className="text-foreground leading-relaxed">
                    {[customer.address, customer.area, customer.city, customer.district, customer.postcode]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CreditCard className="h-4 w-4 text-gold-soft mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Payment Method</p>
                  <span className="rounded border border-gold/20 px-2.5 py-1 text-[11px] text-gold-soft uppercase font-semibold">
                    {order.paymentMethod}
                  </span>
                  {order.bkashTxn && (
                    <p className="mt-1 text-xs font-mono text-gold">TxnID: {order.bkashTxn}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-gold-soft mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Order Date</p>
                  <p className="text-foreground">
                    {new Date(order.createdAt).toLocaleString("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </Panel>

          {/* Communication Actions */}
          {customer.phone && (
            <Panel title="Quick Actions">
              <div className="space-y-3">
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex w-full items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-5 py-3 text-sm font-bold text-white hover:bg-[#20bd5a] transition shadow-sm"
                >
                  <MessageCircle className="h-4 w-4 fill-current" />
                  Message on WhatsApp
                </a>
                <a
                  href={`tel:${customer.phone}`}
                  className="flex w-full items-center justify-center gap-2.5 rounded-full border border-gold/40 bg-accent/40 px-5 py-3 text-sm font-bold text-gold hover:bg-accent/70 transition"
                >
                  <Phone className="h-4 w-4" />
                  Call Customer
                </a>
              </div>
            </Panel>
          )}
        </div>

        {/* RIGHT — Ordered Items + Summary */}
        <div className="lg:col-span-2 space-y-6">
          <Panel title="Ordered Items">
            <div className="space-y-3">
              {order.items.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 rounded-lg border border-gold/10 bg-accent/20 p-3"
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-14 w-14 rounded-md object-cover border border-gold/15 shrink-0"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-md border border-gold/15 bg-accent/50 flex items-center justify-center shrink-0">
                      <ShoppingBag className="h-5 w-5 text-gold-soft" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground text-sm leading-snug">{item.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.ml ? `${item.ml}ml · ` : ""}Qty: {item.qty}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">{fmt(item.price)} each</p>
                    <p className="text-sm font-bold text-gold">{fmt(item.price * item.qty)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Price Breakdown */}
            <div className="mt-6 pt-4 border-t border-gold/15 space-y-2 text-sm max-w-xs ml-auto">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span className="font-semibold text-foreground">{fmt(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-gold">
                  <span>Discount</span>
                  <span className="font-semibold">− {fmt(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-muted-foreground">
                <span>Shipping</span>
                <span className="font-semibold text-foreground">
                  {shippingFee === 0 ? "Free" : fmt(shippingFee)}
                </span>
              </div>
              <div className="flex justify-between border-t border-gold/15 pt-2 text-base font-bold text-gold">
                <span>Total</span>
                <span>{fmt(order.total)}</span>
              </div>
            </div>
          </Panel>

          {/* Package Info placeholder */}
          <Panel>
            <div className="flex items-center gap-3 text-muted-foreground text-sm">
              <Package className="h-5 w-5 text-gold-soft shrink-0" />
              <div>
                <p className="text-foreground font-semibold">Fulfillment</p>
                <p className="text-xs">
                  {order.courier
                    ? `Courier: ${order.courier.provider || "Assigned"} — `
                    : "Courier not assigned yet. "}
                  {order.courier ? (
                    <a
                      href={order.courier.trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-gold hover:underline inline-flex items-center gap-1"
                    >
                      Track shipment <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">Update status to "Shipped" once dispatched.</span>
                  )}
                </p>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
